import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, AlertCircle } from 'lucide-react';
import { api, getPublicImageUrl } from '../../utils/api';
import { toast } from 'sonner';
import Header from '../shared/Header';
import Footer from '../shared/Footer';
import { Checkbox } from '../ui/checkbox';
import SEO from '../shared/SEO';

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const hairstyleId = searchParams.get('hairstyle');
  const [hairstyle, setHairstyle] = useState(null);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [policyAcknowledged, setPolicyAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hairstyleId) {
      api.getHairstyle(hairstyleId)
        .then(res => setHairstyle(res.data))
        .catch(err => {
          toast.error('Failed to load hairstyle');
          navigate('/gallery');
        });
    } else {
      navigate('/gallery');
    }
  }, [hairstyleId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!policyAcknowledged) {
      toast.error('Please acknowledge our policies');
      return;
    }

    setLoading(true);
    try {
      const response = await api.createBooking({
        hairstyle_id: hairstyleId,
        appointment_date: appointmentDate,
        appointment_time: appointmentTime,
        policy_acknowledged: true,
        selected_variant: null,
        add_ons: []
      });
      
      toast.success('Booking created successfully!');
      navigate(`/payment?booking=${response.data.booking.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally {
      setLoading(false);
    }
  };

  if (!hairstyle) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3E2723] border-t-transparent"></div>
      </div>
    );
  }

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-[#FCFBF9]">
      <SEO title="Book Appointment" description="Book your hair appointment at Crowns by Preshy." path="/booking" noindex />
      <Header />

      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h1 className="font-serif text-4xl text-[#3E2723] mb-2 tracking-tight">Book Your Appointment</h1>
            <p className="text-[#786E66]">Complete your booking details below</p>
          </div>

          <div className="grid md:grid-cols-5 gap-8">
            <div className="md:col-span-2">
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#E6DCD3] p-6 sticky top-24" data-testid="booking-summary">
                <h3 className="font-serif text-xl text-[#3E2723] mb-4">Booking Summary</h3>
                {hairstyle.cover_image && (
                  <img src={getPublicImageUrl(hairstyle.cover_image)} alt={hairstyle.name} className="w-full h-48 object-cover rounded-lg mb-4" />
                )}
                <h4 className="font-medium text-[#1A1A1A] mb-2">{hairstyle.name}</h4>
                <p className="text-sm text-[#786E66] mb-4">{hairstyle.category}</p>
                <div className="space-y-2 text-sm mb-4 border-t border-[#E6DCD3] pt-4">
                  <div className="flex justify-between">
                    <span className="text-[#786E66]">Duration:</span>
                    <span className="text-[#1A1A1A]">{hairstyle.duration_minutes} mins</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#786E66]">Price:</span>
                    <span className="text-[#1A1A1A] font-medium">₦{hairstyle.price_ngn.toLocaleString()}</span>
                  </div>
                  {hairstyle.deposit_required && (
                    <div className="flex justify-between">
                      <span className="text-[#786E66]">Deposit Required:</span>
                      <span className="text-[#1A1A1A] font-medium">₦{hairstyle.deposit_required.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="md:col-span-3">
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8" data-testid="booking-form">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Appointment Date</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#786E66]" />
                      <input
                        type="date"
                        value={appointmentDate}
                        onChange={(e) => setAppointmentDate(e.target.value)}
                        min={minDateStr}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none"
                        data-testid="booking-date-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Appointment Time</label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#786E66]" />
                      <input
                        type="time"
                        value={appointmentTime}
                        onChange={(e) => setAppointmentTime(e.target.value)}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none"
                        data-testid="booking-time-input"
                      />
                    </div>
                  </div>

                  <div className="bg-[#F3EBE1] rounded-lg p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <AlertCircle className="h-5 w-5 text-[#3E2723] flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-[#3E2723] mb-2">Important Policies</h4>
                        <ul className="text-sm text-[#786E66] space-y-1 list-disc list-inside">
                          <li>Arrive 10 minutes before your appointment</li>
                          <li>Cancellations must be made 24 hours in advance</li>
                          <li>Late arrivals may result in rescheduling</li>
                          <li>Deposits are non-refundable</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="policy"
                        checked={policyAcknowledged}
                        onCheckedChange={setPolicyAcknowledged}
                        data-testid="policy-checkbox"
                      />
                      <label htmlFor="policy" className="text-sm text-[#1A1A1A] cursor-pointer">
                        I have read and agree to the <a href="/policies" target="_blank" className="text-[#3E2723] underline">cancellation and booking policies</a>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !policyAcknowledged}
                    className="w-full bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full py-3 font-medium transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="booking-submit-button"
                  >
                    {loading ? 'Processing...' : 'Continue to Payment'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BookingPage;
